// src/hooks/useCommonApiHooks.ts
import { useEffect, useState, useCallback, useRef } from "react";
import { gxfileviewApi } from "../apiConfig/services/customerAbstractApiLayer";
import type { FileViewPayload } from "../types/adminMenu";
import type { DocumentViewResponse } from "../types/commonTypes";

// ------------------- Generic Query Hook (auto-fetch on mount / dependency change) -------------------

export const useApiHook = <TPayload, TResponse>(
  apiFn: (payload: TPayload) => Promise<TResponse>,
  payload: TPayload,
  enabled: boolean = true,
) => {
  const [data, setData] = useState<TResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  // FIX: fetchData used to depend on `payload` by object reference. Any
  // call site that passes an inline literal — `useAuthGroups({ portalId,
  // smeCompId })` — creates a brand-new object every render, so that
  // dependency never stabilizes: fetch -> setData/setLoading -> re-render
  // -> new payload object -> fetchData recreated -> effect fires again ->
  // fetch again -> ... forever. That used to be the caller's job to avoid
  // (via useMemo), which is easy to forget and easy to reintroduce.
  //
  // Fix it once, here: key off a serialized snapshot of the payload's
  // *contents* instead of its reference. payloadRef only gets reassigned
  // when the serialized contents actually change, so fetchData (and the
  // effect below) only re-fires on a real payload change, regardless of
  // whether the caller memoized what they passed in.
  const payloadKey = JSON.stringify(payload);
  const payloadRef = useRef(payload);
  const payloadKeyRef = useRef(payloadKey);
  if (payloadKeyRef.current !== payloadKey) {
    payloadKeyRef.current = payloadKey;
    payloadRef.current = payload;
  }

  const fetchData = useCallback(async () => {
    if (!enabled || !payloadRef.current) return;

    try {
      setLoading(true);
      setError(null);

      const res = await apiFn(payloadRef.current);
      setData(res);
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
    // payloadKey (a stable string) stands in for payloadRef.current here —
    // that's the whole point of the fix, so this is intentional rather
    // than a missing-dep oversight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFn, payloadKey, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
};

// ------------------- Generic Mutation Hook (manual trigger, e.g. submit/update) -------------------

/**
 * Unlike useApiHook (which auto-fires on mount/dependency change), this is
 * for calls that should only run when the user explicitly triggers them
 * (e.g. form submit / update). Call `mutate(payload)` from an event handler.
 */
export const useApiMutation = <TPayload, TResponse>(
  apiFn: (payload: TPayload) => Promise<TResponse>,
) => {
  const [data, setData] = useState<TResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const mutate = useCallback(
    async (payload: TPayload) => {
      try {
        setLoading(true);
        setError("");

        const res = await apiFn(payload);
        setData(res);

        return res;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiFn],
  );

  return {
    data,
    loading,
    error,
    mutate,
  };
};

// ------------------- File View -------------------

/**
 * Fetch a previously uploaded SME document's view/details manually.
 *
 * Usage:
 * const { mutate: viewFile, loading, data, error } = useFileView();
 *
 * const response = await viewFile({
 *   documentType: "TRADE LICENSE",
 *   vcifId: "GMEX101104",
 * });
 */
export const useFileView = () =>
  useApiMutation<FileViewPayload, DocumentViewResponse>(gxfileviewApi);
